import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import PropTypes from "prop-types";

import EventService from "service/event/EventService";
import { usePopover } from "components/common/usePopover";
import PatientService, { LANGUAGE_EDITING_ENABLED } from "../service/PatientService";

/**
 * The preferred-languages control under the patient name. Source: Figma
 * "Popover · Preferred languages" (2646:3793) and its add flow (2646:3850).
 *
 * The header line is the trigger; clicking it opens a popover of the patient's
 * languages as chips, each removable (×), plus a "+ Add" that reveals a
 * type-to-add input over the master list. It is a FULL editor (add AND remove),
 * matching bridge's checkbox modal — the Figma only draws the add half.
 *
 * ONLY THE MASTER LIST CAN BE ADDED (product, 2026-09-10). Typing filters it;
 * it never becomes a value. There used to be an `Add "…"` row and an Enter
 * fallback that committed exactly what was typed when nothing matched, which
 * let an operator store "Hindii" or "english " as a language. Both are gone,
 * and add() refuses any code the master list does not hold. A language the
 * patient ALREADY has stays as a chip even if the list lacks it — dropping it
 * on the next save would delete stored data nobody chose to remove.
 *
 * Code vs name: a language is identified by a `code` and shown by a `name`. On
 * dev1 they're the same string, but we don't assume it — chips DISPLAY the name
 * and the save SENDS the codes (bridge posts `.code` under `languageCode`). APIs
 * mirror bridge: master GET /language, current GET /patients/{id}/ppl, save
 * POST /patients/{id}/ppl { patientId, languageCode:[codes] } (full replace).
 */

/** Normalise the master-list response to [{ code, name }] from either an array
 *  or a `{ key: value }` map. When the map value isn't a display string (e.g. a
 *  flag/count), the key doubles as the name — which is the dev1 shape. */
const toOptions = (data) => {
  // Unwrap the common envelopes a master list might arrive in.
  let d = data;
  if (d && !Array.isArray(d) && typeof d === "object") {
    if (Array.isArray(d.languages)) d = d.languages;
    else if (Array.isArray(d.data)) d = d.data;
    else if (Array.isArray(d.items)) d = d.items;
    else if (Array.isArray(d.result)) d = d.result;
    else if (d.data && typeof d.data === "object") d = d.data;
  }
  let raw = [];
  if (Array.isArray(d)) {
    raw = d.map((o) =>
      typeof o === "string"
        ? { code: o, name: o }
        : {
            code: o.code || o.id || o.value || o.name,
            name: o.name || o.value || o.label || o.code,
          }
    );
  } else if (d && typeof d === "object") {
    raw = Object.entries(d).map(([k, v]) => ({
      code: k,
      name: typeof v === "string" && v ? v : k,
    }));
  }
  // Dedupe by code and by name so the typeahead never lists a language twice.
  const seenCode = new Set();
  const seenName = new Set();
  const out = [];
  raw.forEach((o) => {
    if (o.code == null || o.code === "") return;
    const code = String(o.code).trim();
    const name = String(o.name || o.code).trim();
    const nameKey = name.toLowerCase();
    if (!code || seenCode.has(code) || seenName.has(nameKey)) return;
    seenCode.add(code);
    seenName.add(nameKey);
    out.push({ code, name });
  });
  return out;
};

// The kill-switch lives in PatientService, at the network boundary, so nothing
// in phr can POST to /ppl even if this component is bypassed. It was added
// because the endpoint used to duplicate the stored rows instead of replacing
// them, with no verb able to delete a row again; that is FIXED (product,
// 2026-09-10) and the switch is kept only as a way to turn editing off.
// While it is off this popover is VIEW-ONLY: chips render, but there is no
// add / remove / Save, and the master list is never even fetched.
const EDITING_ENABLED = LANGUAGE_EDITING_ENABLED;

const PreferredLanguagesPopover = ({ patientId, languages, languagesState, onSaved }) => {
  const [open, setOpen] = useState(false);
  const { triggerRef, menuRef, menuStyle } = usePopover(open, () => setOpen(false));
  const service = useRef(new PatientService()).current;
  const inputRef = useRef(null);
  /* The add row, INCLUDING its suggestion list — the list is nested inside it,
     so one ref covers both and a click on a suggestion is never read as a click
     outside. */
  const addRowRef = useRef(null);

  // Current languages as { code, name } (name falls back to code), DEDUPED.
  // The /ppl list can carry duplicates — the same code twice, or two rows that
  // display the same name — so collapse on both keys, keeping the first. Without
  // this the chips (and the collapsed header line) repeat a language many times.
  const current = useMemo(() => {
    const seenCode = new Set();
    const seenName = new Set();
    const out = [];
    (languages || []).forEach((l) => {
      if (!l || l.code == null || l.code === "") return;
      const code = String(l.code).trim();
      const name = String(l.value || l.code).trim();
      const nameKey = name.toLowerCase();
      if (!code || seenCode.has(code) || seenName.has(nameKey)) return;
      seenCode.add(code);
      seenName.add(nameKey);
      out.push({ code, name });
    });
    return out;
  }, [languages]);
  const currentCodes = useMemo(() => current.map((c) => c.code), [current]);
  const currentKey = currentCodes.join("|");

  const [options, setOptions] = useState([]); // [{ code, name }]
  // idle | loading | ok | error — the add row says which, instead of going blank.
  const [optionsState, setOptionsState] = useState("idle");
  const [selected, setSelected] = useState(currentCodes); // codes
  const [adding, setAdding] = useState(false);
  const [query, setQuery] = useState("");
  const [saving, setSaving] = useState(false);

  // code → name, from the patient's own list first, then the master list.
  const nameOf = useMemo(() => {
    const map = {};
    current.forEach((c) => {
      map[c.code] = c.name;
    });
    options.forEach((o) => {
      if (!map[o.code]) map[o.code] = o.name;
    });
    return (code) => map[code] || code;
  }, [current, options]);

  // Focus the add input WITHOUT scrolling — the browser's default focus-scroll
  // would move the window, and usePopover closes on scroll, so a plain autoFocus
  // made "+ Add" dismiss the whole popover on shorter viewports.
  useEffect(() => {
    if (adding && inputRef.current) inputRef.current.focus({ preventScroll: true });
  }, [adding]);

  // Opening (or a changed upstream list) reseeds the working copy and collapses
  // the add row, so a cancelled edit never leaks into the next open.
  useEffect(() => {
    if (open) {
      setSelected(currentCodes);
      setAdding(false);
      setQuery("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, currentKey]);

  /* COLLAPSE THE SUGGESTIONS ON A CLICK ELSEWHERE.
     usePopover only knows about clicks outside the WHOLE popover — those close
     it. A click inside the popover but outside the add row reached nothing, so
     the suggestion list stayed open over the chips with no way to dismiss it
     but picking something or closing the popover.

     `mousedown`, matching usePopover, so this runs before the click; and the
     early return on addRowRef is what keeps picking a suggestion working, since
     that mousedown lands inside the row. */
  useEffect(() => {
    if (!adding) return undefined;
    const onDown = (e) => {
      if (addRowRef.current && addRowRef.current.contains(e.target)) return;
      setAdding(false);
      setQuery("");
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [adding]);

  // Master list, fetched once on first open — only when editing is enabled
  // (view-only mode has no typeahead). Silent on failure.
  useEffect(() => {
    if (!open || !EDITING_ENABLED || options.length || optionsState === "loading") return;
    setOptionsState("loading");
    service.getLanguageOptions(
      (data) => {
        setOptions(toOptions(data));
        setOptionsState("ok");
      },
      () => setOptionsState("error")
    );
    /* optionsState is NOT a dependency: an "error" re-running this effect would
       retry in a loop. A failed load is retried on the next open instead. */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, options.length, service]);

  const q = query.trim().toLowerCase();
  // Show the pickable languages (those not already chosen). With no query it's
  // the WHOLE list to browse (like bridge's checkbox list) — not blank until you
  // type — so adding always has something to click even if the query matches
  // nothing. A selected chip is excluded whether it matches by code OR by name.
  const selectedNames = selected.map((c) => nameOf(c).toLowerCase());
  const pickable = options.filter(
    (o) =>
      selected.indexOf(o.code) === -1 &&
      selectedNames.indexOf(o.name.toLowerCase()) === -1
  );
  const suggestions = (q
    ? pickable.filter(
        (o) => o.name.toLowerCase().includes(q) || o.code.toLowerCase().includes(q)
      )
    : pickable
  ).slice(0, 50);
  /* When there is nothing to pick, the list SAYS why — it never offers what was
     typed. (It used to: an `Add "…"` row that stored free text.) */
  const listNote =
    suggestions.length > 0
      ? null
      : optionsState === "error"
      ? "Couldn\u2019t load the language list"
      : optionsState !== "ok"
      ? "Loading languages\u2026"
      : q
      ? "No matching language"
      : null;

  const add = (code) => {
    const c = String(code).trim();
    // Only a code the master list holds — never typed text (see the header).
    if (!c || !options.some((o) => o.code === c)) return;
    setSelected((prev) => (prev.indexOf(c) === -1 ? prev.concat(c) : prev));
    setQuery("");
  };
  const remove = (code) => setSelected((prev) => prev.filter((c) => c !== code));

  const dirty =
    selected.length !== currentCodes.length ||
    selected.slice().sort().join("|") !== currentCodes.slice().sort().join("|");

  const save = () => {
    if (!EDITING_ENABLED) return; // see the switch above
    setSaving(true);
    // Send the DEDUPED distinct codes — a small, BOUNDED list (<= the master
    // list size), never the raw stored /ppl rows. The endpoint replaces the set
    // properly now, but rows written while it was doubling them are still
    // stored, so echoing them back would make the body grow with the table.
    service.savePatientLanguages(
      patientId,
      selected,
      () => {
        setSaving(false);
        setOpen(false);
        if (onSaved) onSaved();
      },
      (err) => {
        setSaving(false);
        EventService.emit("showError", {
          message: `Could not save languages: ${(err && err.message) || ""}`,
        });
      }
    );
  };

  // Collapsed display: "Hindi, English, +N more" (2 shown); empty invites adding.
  const shownNames = currentCodes.map(nameOf);
  /**
   * Four distinct outcomes, four distinct lines. "No preferred languages" is a
   * statement about the PATIENT, so it is only said when the fetch actually
   * succeeded and came back empty — a failure says so instead.
   */
  const label =
    languagesState === "loading" && shownNames.length === 0
      ? "Preferred languages\u2026"
      : languagesState === "error" && shownNames.length === 0
      ? "Preferred languages unavailable"
      : shownNames.length === 0
      ? EDITING_ENABLED
        ? "+ Add preferred languages"
        : "No preferred languages"
      : `Preferred languages - ${
          shownNames.length > 2
            ? `${shownNames.slice(0, 2).join(", ")}, +${shownNames.length - 2} more`
            : shownNames.join(", ")
        }`;

  const SaveButton = (
    <button type="button" className="pl-save" onClick={save} disabled={saving || !dirty}>
      {saving ? "Saving…" : "Save"}
    </button>
  );

  return (
    <>
      <button
        type="button"
        ref={triggerRef}
        className={`ph-langs-trigger${currentCodes.length === 0 ? " is-empty" : ""}${
          languagesState === "error" ? " is-failed" : ""
        }`}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        {label}
      </button>

      {open &&
        ReactDOM.createPortal(
          <div
            ref={menuRef}
            className="pl-pop"
            style={{ top: menuStyle.top, left: menuStyle.left }}
            role="dialog"
            aria-label="Preferred languages"
          >
            <span className="pl-pop-cap">Preferred languages · {selected.length}</span>

            <div className="pl-chips">
              {selected.map((code) => (
                <span className="pl-chip" key={code}>
                  {nameOf(code)}
                  {EDITING_ENABLED && (
                    <button
                      type="button"
                      className="pl-chip-x"
                      aria-label={`Remove ${nameOf(code)}`}
                      onClick={() => remove(code)}
                    >
                      &times;
                    </button>
                  )}
                </span>
              ))}
              {selected.length === 0 && (
                <span className="pl-chips-empty">
                  {EDITING_ENABLED ? "No languages yet" : "No preferred languages"}
                </span>
              )}
              {EDITING_ENABLED && !adding && (
                <button type="button" className="pl-add" onClick={() => setAdding(true)}>
                  + Add
                </button>
              )}
            </div>

            {!EDITING_ENABLED && (
              <span className="pl-note">
                Editing is paused while a backend fix is in progress.
              </span>
            )}

            {EDITING_ENABLED && (adding || dirty) && (
              <span className="pl-divider" aria-hidden="true" />
            )}

            {EDITING_ENABLED &&
              (adding ? (
              <div className="pl-add-row" ref={addRowRef}>
                <div className="pl-input-wrap">
                  <input
                    ref={inputRef}
                    type="text"
                    className="pl-input"
                    placeholder="Type a language to add…"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={(e) => {
                      // Enter commits the top SUGGESTION — a master-list
                      // language — and nothing else; with no match it does nothing.
                      if (e.key === "Enter") {
                        e.preventDefault();
                        if (suggestions.length) add(suggestions[0].code);
                      }
                    }}
                  />
                  {(suggestions.length > 0 || listNote) && (
                    <ul className="pl-suggest" role="listbox">
                      {suggestions.map((s) => (
                        <li key={s.code}>
                          <button type="button" onClick={() => add(s.code)}>
                            {s.name}
                          </button>
                        </li>
                      ))}
                      {listNote && (
                        <li key="__note" className="pl-suggest-empty">
                          {listNote}
                        </li>
                      )}
                    </ul>
                  )}
                </div>
                {SaveButton}
              </div>
            ) : (
              // A removal (no add row) still needs a way to commit.
              dirty && <div className="pl-save-row">{SaveButton}</div>
            ))}
          </div>,
          document.body
        )}
    </>
  );
};

PreferredLanguagesPopover.propTypes = {
  patientId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  /** The patient's languages — [{ code, value }] from /ppl. */
  languages: PropTypes.arrayOf(PropTypes.object),
  /** Called after a successful save so the header can refetch. */
  onSaved: PropTypes.func,
};

PreferredLanguagesPopover.defaultProps = { languages: [] };

export default PreferredLanguagesPopover;
